import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BotIcon, ClockIcon, FileSearchIcon } from 'lucide-react';
import { useAssessment } from '../contexts/AssessmentContext';
import { EVIDENCE_KEY } from '../utils/scoring';
import { EvidenceCategory } from '../types/assessment';
import { BriefingLaw } from './BriefingLaw';
const STAGES = [{
  label: 'Ledger reconciliation',
  timing: '~25 min'
}, {
  label: 'Variance & comparatives',
  timing: '~20 min'
}, {
  label: 'Submission & reflection',
  timing: '~15 min'
}];
const RULES = [{
  icon: ClockIcon,
  title: '60 minutes, one sitting',
  body: "The timer starts when you begin and does not pause. You are not expected to finish everything — choosing what to check is part of the work"
}, {
  icon: BotIcon,
  title: 'Use the AI assistant freely',
  body: 'There is no penalty for using it and no bonus either. It has strong accounting knowledge but no visibility of your documents — it knows only what you paste in.'
}, {
  icon: FileSearchIcon,
  title: 'We measure how you verify',
  body: 'What you checked, against what, and what you took on trust. Polish, formatting, writing quality and speed are not assessed.'
}];
const KEY_ORDER: EvidenceCategory[] = ['resolved', 'clean-verified', 'escalated', 'over-or-incorrect', 'missed'];
export function Briefing() {
  const {
    startAssessment,
    startedAt,
    instrument
  } = useAssessment();
  const navigate = useNavigate();

  // Each instrument brings its own briefing; the engine below is shared.
  if (instrument === 'LAW-001') return <BriefingLaw data-id="element-473" data-mp="at876251nq0k" />;
  const begin = () => {
    startAssessment();
    navigate('/workspace');
  };
  return <main className="w-full flex-1 bg-slate-100 pb-16" data-id="element-474" data-mp="eutivk5udy96">
      <section className="bg-slate-900" data-id="element-475" data-mp="eu9yzxwmszsi">
        <div className="mx-auto max-w-[1100px] px-6 py-14" data-id="element-476" data-mp="2ckuqdhip2gmh">
          <span className="inline-flex items-center rounded-full border border-slate-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-teal-400" data-id="element-477" data-mp="2cme87ck00zqd">
            Staff Accountant · Practical assessment
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-white" data-id="element-478" data-mp="3exzekll90nvc">
            European entity close — Q2 2026 reconciliation and variance review
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-479" data-mp="3ow6t6xs2etu6">
            You are the Staff Accountant for Meridian SAS (France), reporting into group
            consolidation. The Q2 close pack is due to the Group Controller today.
          </p>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-480" data-mp="1r7f0zejo07c">
            Something in this pack does not hold up, and everything foots.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-6" data-id="element-481" data-mp="bjdeeu74ljwk">
        <div className="-mt-8 grid gap-4 sm:grid-cols-3" data-id="element-482" data-mp="pga60xv2j5ei">
          {STAGES.map(s => <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-483" data-mp="ki8k8569crzc">
              <p className="font-mono-num text-xl font-semibold text-slate-900" data-id="element-484" data-mp="1440f0zqotx0i">{s.timing}</p>
              <p className="mt-1 text-sm font-medium text-slate-700" data-id="element-485" data-mp="192lf8z5xwgoy">{s.label}</p>
              <p className="mt-2 text-xs text-slate-500" data-id="element-486" data-mp="190hbqibydjp7">Indicative only — spend your time as you judge best.</p>
            </div>)}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-487" data-mp="z3mfk635jve3">
          {RULES.map(r => <div key={r.title} className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-488" data-mp="ldiyrun1ktw1">
              <r.icon className="h-5 w-5 text-slate-900" data-id="element-489" data-mp="q3pl1nu302b4" />
              <h2 className="mt-3 text-[15px] font-semibold text-slate-900" data-id="element-490" data-mp="q5afzekb5n9i">{r.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-491" data-mp="bgo2lie8r5kx">{r.body}</p>
            </div>)}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-id="element-492" data-mp="gefw2gujazr8">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-493" data-mp="2fpyl03syy2c">How this is read</h2>
          <p className="mt-1.5 text-sm text-slate-600" data-id="element-494" data-mp="7cgdvl7s9pmp">
            Each item in the pack is read against one of these descriptions. There is no score and no
            ranking.
          </p>
          <ul className="mt-4 space-y-2" data-id="element-495" data-mp="3qk4lxv5d5xog">
            {KEY_ORDER.map(k => <li key={k} className={`rounded-lg border px-4 py-2.5 text-[13.5px] font-medium ${EVIDENCE_KEY[k].className}`} data-id="element-496" data-mp="3lsd5idfx98c1">
                {EVIDENCE_KEY[k].label}
              </li>)}
          </ul>
          <p className="mt-4 text-sm text-slate-600" data-id="element-497" data-mp="3cros9kycrc07">
            Polish, formatting, writing quality and speed are not assessed.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-498" data-mp="37r0rw8o8hitb">
          <p className="max-w-md text-sm text-slate-600" data-id="element-499" data-mp="2yrx8f3l5n1ea">
            The clock starts the moment you press this and does not pause. Your work saves
            automatically; if you lose connection, reopen this page and you will resume where you
            left off.
          </p>
          <button type="button" onClick={begin} className="group inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-slate-700" data-id="element-500" data-mp="wypspt1mqhrv">
            {startedAt ? 'Return to assessment' : 'Begin assessment'}
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-501" data-mp="2oos7952nscgd" />
          </button>
        </div>
      </div>
    </main>;
}